<template>
    <div
        class="badge"
        :class="[styleClass, shapeClass, colorClass, isTransparent ? 'bg-transparent border' : undefined, showDot ? 'pl-3' : undefined]"
    >
        <div class="badge-content">
            <!-- Dot -->
            <span v-if="showDot" class="dot" />

            <!-- Icon -->
            <i v-if="showIcon" :class="icon" class="icon-badge" />

            <!-- Text -->
            <span class="label">{{ text }}</span>
        </div>

        <!-- Close button -->
        <button v-if="closeable" class="btn-badge" @click="emitClose">
            <i class="fa-solid fa-xmark"></i>
        </button>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { BadgeStyle, BadgeShape } from "@/utils/enums/badges";
import { ColorAccent } from "@/utils/enums/colors";

export default defineComponent({
    name: "BadgeComponent",
    emits: ["close"],
    props: {
        /* BadgeStyle (border, flat or filled) */
        styleType: {
            type: String as PropType<any>,
            default: BadgeStyle.BORDER,
            validator: (value: BadgeStyle) => Object.values(BadgeStyle).includes(value),
        },
        /* square o circle */
        shape: {
            type: String as PropType<any>,
            default: BadgeShape.BADGE,
            validator: (value: BadgeShape) => Object.values(BadgeShape).includes(value),
        },
        /* color */
        color: {
            type: String as PropType<any>,
            default: typeof ColorAccent !== "undefined" ? ColorAccent.NEUTRAL : undefined,
            validator: (value: any) => (typeof ColorAccent !== "undefined" ? Object.values(ColorAccent).includes(value) : true),
        },
        showDot: { type: Boolean as PropType<boolean>, default: false },
        closeable: { type: Boolean as PropType<boolean>, default: false },
        isTransparent: { type: Boolean as PropType<boolean>, default: false },
        showIcon: { type: Boolean as PropType<boolean>, default: false },
        text: { type: String as PropType<string>, default: "Badge" },
        icon: { type: String as PropType<any>, default: "fa-solid fa-circle" },
    },
    methods: {
        emitClose: function () {
            this.$emit("close");
        },
    },
    computed: {
        styleClass: function (): string | Record<string, boolean> {
            return (
                {
                    [BadgeStyle.BORDER]: "border",
                    [BadgeStyle.FLAT]: "flat",
                    [BadgeStyle.FILLED]: "filled",
                }[this.styleType] || "border"
            );
        },

        shapeClass: function (): string {
            const shapeVariants: Record<string, string> = {
                [BadgeShape.BADGE]: "rounded",
                [BadgeShape.SQUARE]: "square",
                [BadgeShape.PILL]: "rounded-full",
            };
            return shapeVariants[this.shape as string] || "rounded";
        },

        colorClass: function (): string {
            const colorVariant: Record<string, string> = {
                [ColorAccent.NEUTRAL]: "neutral",
                [ColorAccent.SUCCESS]: "success",
                [ColorAccent.WARNING]: "warning",
                [ColorAccent.DANGER]: "danger",
                [ColorAccent.INFO]: "info",
                [ColorAccent.PRIMARY_BRAND]: "primary-brand",
                [ColorAccent.SECONDARY_BRAND]: "secondary-brand",
            };

            return colorVariant[this.color as string] || "neutral";
        },
    },
});
</script>

<style scoped>
@import "@/style/components/badge.css";
</style>
